import { useState, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitleDefault from '../default/DialogTitleDefault';
import { Box, Card, Divider, Grid, TextField, Typography, useTheme, keyframes } from '@mui/material';
import { ButtonBasic } from '../../components';
import ConsultasService from '../../services/consultas.service';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

// Animaciones
const flipCard = keyframes`
    0% { transform: rotateY(0deg); }
    50% { transform: rotateY(180deg); }
    100% { transform: rotateY(360deg); }
`;

const pulseGlow = keyframes`
    0% { box-shadow: 0 0 5px rgba(100, 181, 246, 0.5); }
    50% { box-shadow: 0 0 20px rgba(100, 181, 246, 0.8), 0 0 30px rgba(100, 181, 246, 0.4); }
    100% { box-shadow: 0 0 5px rgba(100, 181, 246, 0.5); }
`;

const shimmer = keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
`;

const bounceIn = keyframes`
    0% { transform: scale(0); opacity: 0; }
    50% { transform: scale(1.2); }
    100% { transform: scale(1); opacity: 1; }
`;

const ModalValidarTarjeta = ({show, hideModal}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const handleClose = () => hideModal();
    const [isLoading, setIsLoading] = useState(false);
    const [codigoCard, setCodigoCard] = useState('');
    const [resultado, setResultado] = useState(null);

    // Función para verificar si la tarjeta es válida
    const isCardValid = (estado) => {
        if (!estado) return false;
        const estadoLower = estado.toLowerCase();
        // Primero verificamos si contiene "invalida" o "inválida"
        if (estadoLower.includes('invalida') || estadoLower.includes('inválida')) {
            return false;
        }
        // Si no es inválida, verificamos si contiene "válida" o "valida"
        return estadoLower.includes('válida') || estadoLower.includes('valida') || estadoLower.includes('válid') || estadoLower.includes('valid');
    };

    const onSubmit = async() => {
        setIsLoading(true);
        setResultado(null);
        await ConsultasService.validarTarjeta({codigo: codigoCard}).then((resp)=>{
            setResultado(resp)
            setIsLoading(false)
        }).catch(()=>{
            setIsLoading(false)
        })
    }
    const styleInputHeight = useMemo(() => ({
        '& .MuiInputLabel-root':{
            color: isDarkMode ? '#b0bec5' : 'gray',
            fontSize: 15
        },
        '& fieldset': {
            borderRadius: 2,
            border: 1,
            borderColor: isDarkMode ? '#3d4a62' : 'gray',
            height: 45
        },
        '& label':{ marginTop: -0.5 },
        '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
                border:1,
                height: 45,
                borderColor: isDarkMode ? '#64B5F6' : 'gray'
            },
            '&:hover':{
                border: 1,
                borderRadius: 2,
                borderColor: isDarkMode ? '#64B5F6' : 'gray',
                height: 40
            },
            '&.Mui-focused': {
                '&:hover':{
                    border:0,
                    height: 40
                }
            },
            color: isDarkMode ? '#fff' : 'gray',
            height: 40
        }
    }), [isDarkMode])
    return(
        <Dialog open={show} disableEscapeKeyDown maxWidth='xs' fullWidth sx={{backdropFilter: 'blur(0.4px);'}}>
            <Box sx={{background: isDarkMode ? '#1F2A40' : '#fff'}}>
                <DialogTitleDefault id="add-canal-priv" onClose={handleClose}>
                    <Typography sx={{flex:1,textAlign: 'center', color: isDarkMode ? '#fff' : '#000',fontWeight: 'bold'}} variant='h5'>
                        VALIDAR TARJETA
                    </Typography>
                </DialogTitleDefault>
                <Divider sx={{background: isDarkMode ? '#3d4a62' : 'gray'}} />
                <DialogContent>
                    <Grid container spacing={1}>
                        {/* Animación de tarjeta */}
                        <Grid item xs={12}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                py: 2
                            }}>
                                <Box sx={{
                                    width: 220,
                                    height: 130,
                                    borderRadius: 2,
                                    background: isDarkMode 
                                        ? 'linear-gradient(135deg, #1e3a5f 0%, #2d3a52 50%, #3d4a62 100%)'
                                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    p: 1.5,
                                    animation: isLoading 
                                        ? `${flipCard} 1.5s ease-in-out infinite, ${pulseGlow} 1.5s ease-in-out infinite`
                                        : 'none',
                                    transformStyle: 'preserve-3d',
                                    boxShadow: isDarkMode 
                                        ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                                        : '0 8px 32px rgba(102, 126, 234, 0.4)',
                                    transition: 'all 0.3s ease',
                                    '&::before': isLoading ? {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                                        backgroundSize: '200% 100%',
                                        animation: `${shimmer} 1.5s infinite`,
                                    } : {}
                                }}>
                                    {/* Chip de la tarjeta */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box sx={{
                                            width: 35,
                                            height: 25,
                                            borderRadius: 0.5,
                                            background: 'linear-gradient(135deg, #ffd700 0%, #ffb347 100%)',
                                            boxShadow: 'inset 0 0 3px rgba(0,0,0,0.2)'
                                        }} />
                                        <CreditCardIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 28 }} />
                                    </Box>
                                    
                                    {/* Número de tarjeta - muestra el código ingresado */}
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                                        <Typography 
                                            sx={{ 
                                                color: 'rgba(255,255,255,0.9)', 
                                                fontSize: codigoCard.length > 16 ? 9 : 11, 
                                                fontFamily: 'monospace',
                                                letterSpacing: 1,
                                                textAlign: 'center',
                                                wordBreak: 'break-all',
                                                maxWidth: '100%'
                                            }}
                                        >
                                            {codigoCard.length > 0 
                                                ? codigoCard.replace(/(.{4})/g, '$1 ').trim()
                                                : '•••• •••• •••• ••••'
                                            }
                                        </Typography>
                                    </Box>
                                    
                                    {/* Texto inferior */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ 
                                            color: resultado && !isLoading
                                                ? (isCardValid(resultado?.estado)
                                                    ? (isDarkMode ? '#4caf50' : '#a5d6a7')
                                                    : (isDarkMode ? '#f44336' : '#ef9a9a'))
                                                : 'rgba(255,255,255,0.7)', 
                                            fontSize: 12, 
                                            textTransform: 'uppercase',
                                            fontWeight: resultado && !isLoading ? 'bold' : 'normal',
                                            transition: 'all 0.3s ease',
                                            textShadow: resultado && !isLoading ? '0 1px 2px rgba(0,0,0,0.5)' : 'none'
                                        }}>
                                            {isLoading ? 'Validando...' : (resultado?.estado || 'Tarjeta de Validación')}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {resultado && !isLoading ? (
                                                isCardValid(resultado?.estado) ? (
                                                    <CheckCircleIcon sx={{ 
                                                        color: isDarkMode ? '#4caf50' : '#a5d6a7', 
                                                        fontSize: 20,
                                                        animation: `${bounceIn} 0.5s ease-out`,
                                                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                                                    }} />
                                                ) : (
                                                    <CancelIcon sx={{ 
                                                        color: isDarkMode ? '#f44336' : '#ef9a9a', 
                                                        fontSize: 20,
                                                        animation: `${bounceIn} 0.5s ease-out`,
                                                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                                                    }} />
                                                )
                                            ) : (
                                                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>
                                                    {isLoading ? '••/••' : 'MM/YY'}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                        
                        <Grid item xs={12}>
                            <Typography sx={{color: isDarkMode ? '#e0e0e0' : '#000'}}>Ingrese el codigo</Typography>
                            <TextField
                                fullWidth
                                autoComplete='off'
                                sx={styleInputHeight}
                                value={codigoCard}
                                onChange={(e)=>setCodigoCard(e.target.value)}
                            />
                        </Grid>
                        {resultado?.estado && (
                            <Grid item xs={12}>
                                <Card sx={{background: isDarkMode ? '#2d3a52 !important' : '#fff !important', boxShadow: 4, borderRadius: 2, padding: 1, border: isDarkMode ? '1px solid #3d4a62' : 'none'}}>
                                    <Box sx={{display: 'flex', flexDirection: 'row', marginTop: 0.5, marginLeft: 1}}>
                                        <Typography sx={{color: isDarkMode ? '#e0e0e0' : '#000'}} component='div'>Estado: <Box fontWeight='bold' display='inline' sx={{color: isDarkMode ? '#fff' : '#000'}}>{resultado?.estado}</Box></Typography>
                                    </Box>
                                    <Box sx={{display: 'flex', flexDirection: 'row', marginTop: 0.5, marginLeft: 1}}>
                                        <Typography sx={{color: isDarkMode ? '#e0e0e0' : '#000'}} component='div'>Tipo: <Box fontWeight='bold' display='inline' sx={{color: isDarkMode ? '#fff' : '#000'}}>{resultado?.tipo}</Box></Typography>
                                    </Box>
                                    <Box sx={{display: 'flex', flexDirection: 'row', marginTop: 0.5, marginLeft: 1}}>
                                        <Typography sx={{color: isDarkMode ? '#e0e0e0' : '#000'}} component='div'>Codigo: <Box fontWeight='bold' display='inline' sx={{color: isDarkMode ? '#fff' : '#000'}}>{resultado?.codigo}</Box></Typography>
                                    </Box>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <Divider sx={{background: isDarkMode ? '#3d4a62' : 'gray'}} />
                <DialogActions sx={{display:'flex', justifyContent:'center'}}>
                    <ButtonBasic
                        textButton='Cancelar'
                        variant='wk'
                        color='cancel'
                        onClick={()=>handleClose()}
                        isLoading={isLoading}
                    />
                    <ButtonBasic
                        textButton='Validar'
                        onClick={()=>onSubmit()}
                        isLoading={isLoading}
                        variant='wk'
                        color='nextStep'
                        isDisabled={!codigoCard || codigoCard?.length < 5}
                    />
                </DialogActions>
            </Box>
        </Dialog>
    )
}

export default ModalValidarTarjeta